import {
  useEffect,
  useState,
} from "react";

import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { PageHeader } from "@/components/Page";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import {
  CreditCard,
  CheckCircle2,
  Users,
  Sparkles,
  BadgeCheck,
  Settings,
  Clock3,
  ShieldCheck,
} from "lucide-react";

import { toast } from "sonner";


const plans = [
  {
    key: "pro",
    name: "Farm Hand Pro",
    price: "€20",
    interval: "/month",
    description:
      "For farmers who want the full Farm Hand nutrition and farm-management toolkit.",
    icon: Sparkles,
    features: [
      "Nutrition Evaluation",
      "AI Nutrition Advisor",
      "Feed Budget",
      "Feed-label scanning",
      "Advanced feed analysis",
      "Full Farm Hand farmer toolkit",
    ],
  },

  {
    key: "advisor",
    name: "Farm Hand Consultant",
    price: "€800",
    interval: "/year",
    description:
      "For consultants and advisors managing multiple farms and professional workflows.",
    icon: Users,
    features: [
      "Everything in Farm Hand Pro",
      "Consultant access",
      "Multiple client farms",
      "Professional nutrition tools",
      "Advisor workflows",
      "Advanced reporting as released",
    ],
  },
];


function planLabel(plan) {
  if (plan === "pro") {
    return "Farm Hand Pro";
  }

  if (
    plan === "advisor" ||
    plan === "consultant"
  ) {
    return "Farm Hand Consultant";
  }

  return "No active subscription";
}


function statusLabel(status) {
  if (status === "trialing") {
    return "7-Day Free Trial";
  }

  if (status === "active") {
    return "Active";
  }

  if (status === "past_due") {
    return "Payment Required";
  }

  if (
    status === "cancelled" ||
    status === "canceled"
  ) {
    return "Cancelled";
  }

  if (status === "incomplete") {
    return "Incomplete";
  }

  if (status === "unpaid") {
    return "Unpaid";
  }

  return "Inactive";
}


function formatDate(value) {
  if (!value) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat(
      "en-IE",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    ).format(
      new Date(value)
    );
  } catch {
    return null;
  }
}


export default function Billing() {
  const [loadingPlan, setLoadingPlan] =
    useState(null);

  const [portalLoading, setPortalLoading] =
    useState(false);

  const [accessMessage, setAccessMessage] =
    useState(null);


  useEffect(() => {
    const message =
      sessionStorage.getItem(
        "fh_subscription_message"
      );

    if (message) {
      setAccessMessage(
        message
      );

      sessionStorage.removeItem(
        "fh_subscription_message"
      );
    }
  }, []);


  const {
    data: billing,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "billing-status",
    ],

    queryFn: async () => {
      const { data } =
        await api.get(
          "/billing/status"
        );

      return data;
    },

    refetchOnWindowFocus: true,
  });


  const startCheckout =
    async (plan) => {
      setLoadingPlan(
        plan
      );

      try {
        const { data } =
          await api.post(
            "/billing/checkout",
            {
              plan,
            }
          );

        if (
          !data?.checkout_url
        ) {
          throw new Error(
            "Stripe checkout URL was not returned."
          );
        }

        window.location.href =
          data.checkout_url;

      } catch (error) {
        console.error(
          "Stripe checkout error:",
          error
        );

        const detail =
          error?.response?.data?.detail ||
          "Could not start checkout. Please try again.";

        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message ||
              "Could not start checkout. Please try again."
        );

      } finally {
        setLoadingPlan(
          null
        );
      }
    };


  const openPortal =
    async () => {
      setPortalLoading(
        true
      );

      try {
        const { data } =
          await api.post(
            "/billing/portal"
          );

        if (
          !data?.portal_url
        ) {
          throw new Error(
            "Stripe portal URL was not returned."
          );
        }

        window.location.href =
          data.portal_url;

      } catch (error) {
        console.error(
          "Stripe customer portal error:",
          error
        );

        const detail =
          error?.response?.data?.detail ||
          "Could not open subscription management.";

        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message ||
              "Could not open subscription management."
        );

      } finally {
        setPortalLoading(
          false
        );
      }
    };


  const currentPlan =
    billing?.plan || null;

  const currentStatus =
    billing?.status ||
    "inactive";


  const isTrialing =
    currentStatus ===
    "trialing";


  const isPaidActive =
    currentStatus ===
    "active";


  const hasAccess =
    billing?.is_active ===
    true;


  const canManageSubscription =
    billing?.can_manage_subscription ===
    true;


  const trialEnd =
    formatDate(
      billing?.trial_end
    );


  const periodEnd =
    formatDate(
      billing?.current_period_end
    );


  const hasSubscriptionRecord =
    Boolean(
      currentPlan
    ) &&
    currentPlan !==
      "free";


  return (
    <div>

      <PageHeader
        title="Subscription & Billing"
        subtitle="Manage your Farm Hand plan and subscription."
      />


      {/* Subscription redirect message */}

      {accessMessage && (
        <Card className="p-4 mb-6 border-primary/30 bg-primary/[0.05]">

          <div className="flex items-start gap-3">

            <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />

            <div>

              <p className="font-semibold text-[hsl(148,38%,16%)]">
                Subscription required
              </p>

              <p className="text-sm text-muted-foreground mt-1">
                {accessMessage}
              </p>

            </div>

          </div>

        </Card>
      )}


      {/* Current plan */}

      <Card className="p-6 mb-6">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">

          <div className="flex items-start gap-4">

            <div className="rounded-full bg-primary/10 p-3">

              {isTrialing ? (
                <Clock3 className="h-6 w-6 text-primary" />
              ) : (
                <BadgeCheck className="h-6 w-6 text-primary" />
              )}

            </div>


            <div>

              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                Current Plan
              </p>


              <h2 className="font-display text-2xl font-bold text-[hsl(148,38%,16%)] mt-1">

                {isLoading
                  ? "Loading…"
                  : planLabel(
                      currentPlan
                    )}

              </h2>


              {!isLoading && (

                <p className="text-sm text-muted-foreground mt-1">

                  Subscription status:{" "}

                  <span className="font-semibold text-[hsl(148,38%,16%)]">

                    {statusLabel(
                      currentStatus
                    )}

                  </span>

                </p>

              )}


              {isTrialing &&
                trialEnd && (

                <p className="text-sm text-muted-foreground mt-1">

                  Free trial ends{" "}

                  <span className="font-medium text-[hsl(148,38%,16%)]">
                    {trialEnd}
                  </span>

                </p>

              )}


              {isPaidActive &&
                periodEnd &&
                billing?.cancel_at_period_end && (

                <p className="text-sm text-muted-foreground mt-1">

                  Access continues until{" "}

                  <span className="font-medium text-[hsl(148,38%,16%)]">
                    {periodEnd}
                  </span>

                </p>

              )}

            </div>

          </div>


          {canManageSubscription && (

            <Button
              variant="outline"
              className="rounded-full"
              disabled={
                portalLoading
              }
              onClick={
                openPortal
              }
            >

              <Settings className="h-4 w-4 mr-2" />

              {portalLoading
                ? "Opening…"
                : "Manage Subscription"}

            </Button>

          )}

        </div>


        {/* Trial active */}

        {isTrialing && (

          <div className="mt-5 rounded-lg bg-primary/[0.05] p-4 text-sm text-[hsl(148,38%,16%)]">

            <div className="flex items-center gap-2">

              <Clock3 className="h-4 w-4 text-primary" />

              <span className="font-semibold">
                Your 7-day free trial is active.
              </span>

            </div>


            <p className="text-muted-foreground mt-1 ml-6">

              You currently have full access to Farm Hand.

              {trialEnd && (
                <>
                  {" "}Your trial ends on {trialEnd}.
                </>
              )}

              {" "}
              Your paid subscription will continue automatically unless you cancel before the trial ends.

            </p>

          </div>

        )}


        {/* Paid active */}

        {isPaidActive && (

          <div className="mt-5 rounded-lg bg-primary/[0.05] p-4 text-sm text-[hsl(148,38%,16%)]">

            <div className="flex items-center gap-2">

              <CheckCircle2 className="h-4 w-4 text-primary" />

              <span className="font-semibold">
                Your subscription is active.
              </span>

            </div>


            <p className="text-muted-foreground mt-1 ml-6">
              Your Farm Hand features are available on this account.
            </p>

          </div>

        )}


        {/* Never subscribed */}

        {!isLoading &&
          !hasAccess &&
          !hasSubscriptionRecord && (

          <div className="mt-5 rounded-lg border border-dashed p-4 text-sm">

            <div className="flex items-center gap-2 text-[hsl(148,38%,16%)]">

              <ShieldCheck className="h-4 w-4 text-primary" />

              <span className="font-semibold">
                Start with 7 days free.
              </span>

            </div>


            <p className="text-muted-foreground mt-1 ml-6">
              Choose a plan below to begin your free trial.
            </p>

          </div>

        )}


        {/* Expired / inactive */}

        {!isLoading &&
          !hasAccess &&
          hasSubscriptionRecord && (

          <div className="mt-5 rounded-lg border p-4 text-sm">

            <p className="font-semibold text-[hsl(148,38%,16%)]">
              Your Farm Hand access is not currently active.
            </p>


            <p className="text-muted-foreground mt-1">
              Manage your subscription or choose a plan below to restore access.
            </p>

          </div>

        )}

      </Card>


      {/* Plans */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {plans.map(
          (plan) => {

            const Icon =
              plan.icon;

            const busy =
              loadingPlan ===
              plan.key;

            const isCurrentPlan =
              currentPlan ===
                plan.key ||
              (
                plan.key ===
                  "advisor" &&
                currentPlan ===
                  "consultant"
              );


            return (

              <Card
                key={
                  plan.key
                }
                className={`p-6 flex flex-col ${
                  isCurrentPlan &&
                  hasAccess
                    ? "border-primary ring-1 ring-primary/20"
                    : ""
                }`}
              >


                <div className="flex items-start justify-between gap-4">

                  <div>

                    <div className="flex flex-wrap items-center gap-2">

                      <Icon className="h-5 w-5 text-primary" />


                      <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                        {plan.name}
                      </h2>


                      {isCurrentPlan &&
                        hasAccess && (

                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">

                          {isTrialing
                            ? "Free trial"
                            : "Current plan"}

                        </span>

                      )}

                    </div>


                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {plan.description}
                    </p>

                  </div>


                  <CreditCard className="h-5 w-5 text-muted-foreground shrink-0" />

                </div>


                <div className="mt-6">

                  <span className="font-display text-4xl font-extrabold text-[hsl(148,38%,16%)]">
                    {plan.price}
                  </span>

                  <span className="text-sm text-muted-foreground ml-1">
                    {plan.interval}
                  </span>

                </div>


                {!hasAccess && (

                  <p className="text-sm font-medium text-primary mt-2">

                    7 days free, then{" "}
                    {plan.price}
                    {plan.interval}

                  </p>

                )}


                <ul className="space-y-3 mt-6 flex-1">

                  {plan.features.map(
                    (feature) => (

                      <li
                        key={
                          feature
                        }
                        className="flex items-start gap-2 text-sm"
                      >

                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />

                        <span className="text-[hsl(148,38%,16%)]">
                          {feature}
                        </span>

                      </li>

                    )
                  )}

                </ul>


                {isCurrentPlan &&
                hasAccess ? (

                  <Button
                    variant="outline"
                    className="w-full rounded-full mt-8"
                    disabled
                  >

                    {isTrialing
                      ? "Free Trial Active"
                      : "Current Plan"}

                  </Button>

                ) : (

                  <Button
                    className="w-full rounded-full mt-8"
                    disabled={
                      loadingPlan !==
                        null ||
                      isLoading
                    }
                    onClick={() =>
                      startCheckout(
                        plan.key
                      )
                    }
                  >

                    {busy
                      ? "Opening checkout…"
                      : billing?.trial_available === false
                        ? `Choose ${plan.name}`
                        : "Start 7-Day Free Trial"}

                  </Button>

                )}

              </Card>

            );

          }
        )}

      </div>


      {/* Payment information */}

      <Card className="p-5 mt-6 bg-secondary/40">

        <div className="flex items-start gap-3">

          <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />


          <div>

            <p className="font-semibold text-[hsl(148,38%,16%)]">
              Secure billing through Stripe
            </p>


            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">

              Payments are processed securely by Stripe.
              Farm Hand does not store your card details.
              You can manage or cancel your subscription through Stripe.

            </p>

          </div>

        </div>

      </Card>


      <div className="mt-4">

        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            refetch()
          }
        >
          Refresh subscription status
        </Button>

      </div>

    </div>
  );
}